<template>
  <div class="d-flex flex-column ga-8">
    <v-row>
      <v-col cols="6">
        <v-select v-model="selectedYear" :items="years" density="compact" hide-details variant="solo" />
      </v-col>

      <v-col cols="6">
        <v-select v-model="selectedMonth" :items="months" density="compact" hide-details variant="solo" />
      </v-col>
    </v-row>

    <div class="">
      <div class="d-flex align-center justify-space-between">
        <p>{{ $t('week') }}</p>
        <p>{{ $t('gebruikt-totaal') }}</p>
      </div>

      <div v-if="loading" class="d-flex flex-column ga-4 mt-6">
        <div v-for="i in 4" :key="i" class="">
          <v-skeleton-loader type="list-item" />
        </div>
      </div>

      <div v-else class="d-flex flex-column ga-4 mt-6">
        <div v-for="(credit, index) in credits" :key="index" class="">
          <div class="d-flex align-center justify-space-between">
            <p class="d-block d-md-none text-bold text-capitalize">{{  convertWeekToStringMobile(credit.week_number) }}</p>
            <p class="d-none d-md-block text-bold text-capitalize">{{  convertWeekToString(credit.week_number) }}</p>
            <p>{{ credit.credits }}/{{ max }}</p>
          </div>

          <v-progress-linear class="rounded-sm" :max="max" :model-value="credit.credits" color="var(--company-primary)"></v-progress-linear>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CreditsService from "../../../services/CreditsService";
export default {
  data() {
    return {
      selectedYear: null,
      selectedMonth: null,

      loading: true,
      credits: [],
    };
  },

  props: {
    max: {
      type: Number,
      required: true,
    },
  },

  mounted() {
    this.selectedYear = new Date().getFullYear();
    this.selectedMonth = this.months[0];

    this.getCredits();
  },

  computed: {
    years() {
      const currentYear = new Date().getFullYear();

      // make an array of this year and the next 3 years
      const years = [];
      for (let i = 0; i < 4; i++) {
        years.push(currentYear + i);
      }
      return years;
    },

    months() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth(); // getMonth() returns 0-11

      // If selected year is current year, return current month + remaining months
      if (this.selectedYear === currentYear) {
        const months = [];
        for (let i = currentMonth; i < 12; i++) {
          let monthName = new Date(2025, i).toLocaleDateString(this.$i18n.locale, { month: "long" });
          monthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
          months.push(monthName);
        }
        return months;
      }

      // For other years, return all 12 months
      const months = [];
      for (let i = 0; i < 12; i++) {
        let monthName = new Date(2025, i).toLocaleDateString(this.$i18n.locale, { month: "long" });
        monthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
        months.push(monthName);
      }
      return months;
    },

    weeks() {
      if (!this.selectedYear || !this.selectedMonth) return [];

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();

      // Get the month index from selectedMonth string
      const selectedMonthIndex = this.getMonthIndexFromName(this.selectedMonth);
      if (selectedMonthIndex === -1) return [];

      // Get all weeks in the selected month
      const weeksInMonth = this.getWeeksInMonth(this.selectedYear, selectedMonthIndex);

      // If selected month is current month in current year, filter to current and future weeks
      if (this.selectedYear === currentYear && selectedMonthIndex === currentMonth) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return weeksInMonth.filter((week) => {
          // Keep weeks where the end date is today or in the future
          return week.endDate >= today;
        });
      }

      // For future months, return all weeks
      return weeksInMonth;
    },
  },

  methods: {
    async getCredits() {
      try {
        this.loading = true;
        const monthIndex = this.getMonthIndexFromName(this.selectedMonth);
        const response = await CreditsService.getWeeklyCredits(this.selectedYear, monthIndex);

        if (response) {
          this.credits = response.data.data;
          this.loading = false;
        }
      } catch (error) {
        console.error("Error getting credits", error);
        this.$toast.error("Er is iets fout gegaan bij het ophalen van de credits");
      }
    },
    getMonthIndexFromName(monthName) {
      // Find the month index by comparing month names
      for (let i = 0; i < 12; i++) {
        let testMonthName = new Date(2025, i).toLocaleDateString(this.$i18n.locale, { month: "long" });
        testMonthName = testMonthName.charAt(0).toUpperCase() + testMonthName.slice(1);
        if (testMonthName === monthName) {
          return i + 1;
        }
      }
      return -1;
    },

    getWeeksInMonth(year, month) {
      const weeks = [];
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      // Start from the Monday of the week containing the first day of the month
      let weekStart = new Date(firstDay);
      const dayOfWeek = weekStart.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust to Monday
      weekStart.setDate(weekStart.getDate() + diff);

      while (weekStart <= lastDay) {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6); // Sunday

        // Only include weeks that have at least one day in the selected month
        if (weekEnd >= firstDay && weekStart <= lastDay) {
          const weekNumber = this.getWeekNumber(weekStart);
          const startStr = weekStart.toLocaleDateString(this.$i18n.locale, { day: "numeric", month: "short" });
          const endStr = weekEnd.toLocaleDateString(this.$i18n.locale, { day: "numeric", month: "short" });

          weeks.push({
            name: this.$t('week-weeknumber-startstr-endstr', [weekNumber, startStr, endStr]),
            startDate: new Date(weekStart),
            endDate: new Date(weekEnd),
            weekNumber: weekNumber,
          });
        }

        // Move to next week
        weekStart.setDate(weekStart.getDate() + 7);
      }

      return weeks;
    },

    getWeekNumber(date) {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    },

    convertWeekToString(weekNumber) {
      const year = new Date().getFullYear();
      const start = new Date(year, 0, 1 + (weekNumber - 1) * 7);

      // Move back to Monday
      const day = start.getDay();
      const diff = start.getDate() - (day === 0 ? 6 : day - 1);
      start.setDate(diff);

      const end = new Date(start);
      end.setDate(start.getDate() + 6);

      const fmt = (d) => {
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
      };

      return this.$t('week-weeknumber-startstr-endstr', [weekNumber, fmt(start), fmt(end)]);
    },

    convertWeekToStringMobile(weekNumber) {
      const year = new Date().getFullYear();
      const start = new Date(year, 0, 1 + (weekNumber - 1) * 7);

      // Move back to Monday
      const day = start.getDay();
      const diff = start.getDate() - (day === 0 ? 6 : day - 1);
      start.setDate(diff);

      const end = new Date(start);
      end.setDate(start.getDate() + 6);

      const fmt = (d) => {
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
      };

      return `${fmt(start)} - ${fmt(end)}`;
    },
  },

  watch: {
    selectedYear() {
      this.selectedMonth = this.months[0];
      this.getCredits();
    },
    selectedMonth() {
      this.getCredits();
    }
  },
};
</script>
